import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

// The same key and the same browser-chrome colours as public/theme-init.js, which applies the
// theme before React runs; this module keeps it current afterwards.
const storageKey = 'ges.theme';
const chromeColour: Record<Theme, string> = { light: '#edf2f3', dark: '#0e1c1d' };
const darkQuery = '(prefers-color-scheme: dark)';

function readChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(choice: ThemeChoice, systemDark: boolean): Theme {
  if (choice === 'system') return systemDark ? 'dark' : 'light';
  return choice;
}

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', chromeColour[theme]);
}

interface ThemeState { choice: ThemeChoice; theme: Theme }

const media = window.matchMedia(darkQuery);
let state: ThemeState = (() => {
  const choice = readChoice();
  return { choice, theme: resolveTheme(choice, media.matches) };
})();
const listeners = new Set<() => void>();
// public/theme-init.js has set this before the first paint; applying it again costs nothing and
// keeps the page right should that script ever fail to load.
apply(state.theme);

function publish(next: ThemeState) {
  state = next;
  apply(next.theme);
  listeners.forEach((listener) => listener());
}

// "System" follows the operating system while the portal is open, not only at load.
media.addEventListener?.('change', (event) => {
  if (state.choice === 'system') publish({ choice: 'system', theme: event.matches ? 'dark' : 'light' });
});

export function setThemeChoice(choice: ThemeChoice) {
  try {
    if (choice === 'system') window.localStorage.removeItem(storageKey);
    else window.localStorage.setItem(storageKey, choice);
  } catch {
    // Storage blocked (private window, policy): the choice holds for this page only.
  }
  publish({ choice, theme: resolveTheme(choice, media.matches) });
}

export function useTheme() {
  const current = useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
    () => state,
  );
  return { ...current, setChoice: setThemeChoice };
}
